/* drl-countdown — keeps a server-rendered countdown ticking.

   The first paint already carries the correct figures (Liquid computed them),
   so this element never writes "--" and never shifts the layout. It only
   corrects for the gap between the server's clock and the visitor's, and then
   ticks: every second when seconds are shown, once a minute when they are not.
   A page that sits open for an hour with a seconds-less clock should not wake
   up 3600 times to redraw nothing.

   Used by sections/drl-countdown.liquid (the recruitment strip) and by
   snippets/drl-promo-countdown.liquid (the promo clock in the bar, the cover
   pill and the PDP row). The promo forms add three things, all opt-in via
   markup: a days unit that hides itself at zero ([data-cd-days-unit]), a soft
   tick on the digit that changed (data-tick="soft"), and an `expired` event
   plus class so the surface around the clock can stop rotating. */

class DrlCountdown extends HTMLElement {
  connectedCallback() {
    // data-epoch (unix seconds, from Liquid) needs no parsing; data-target is
    // the ISO string the recruitment strip still renders. `+0200` without a
    // colon is not ECMAScript date syntax and Safari has returned NaN for it.
    const epoch = Number(this.dataset.epoch);
    const target = epoch > 0 ? epoch * 1000 : Date.parse(this.dataset.target);
    if (Number.isNaN(target)) return;

    this.target = target;
    this.withSeconds = this.dataset.seconds === 'true';
    this.softTick = this.dataset.tick === 'soft';
    this.units = this.querySelector('[data-cd-units]');
    this.expired = this.querySelector('[data-cd-expired]');
    this.daysUnit = this.querySelectorAll('[data-cd-days-unit], .drl-pcd__sep--days');
    this.out = {
      days: this.querySelector('[data-cd-days]'),
      hours: this.querySelector('[data-cd-hours]'),
      minutes: this.querySelector('[data-cd-minutes]'),
      seconds: this.querySelector('[data-cd-seconds]'),
    };

    this.tick();
    this.timer = setInterval(() => this.tick(), this.withSeconds ? 1000 : 60000);

    // A phone that slept for an hour comes back with a stale clock; the interval
    // does not fire while the tab is hidden.
    this.onVisible = () => {
      if (!document.hidden) this.tick();
    };
    document.addEventListener('visibilitychange', this.onVisible);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  tick() {
    let left = Math.floor((this.target - Date.now()) / 1000);

    if (left <= 0) {
      clearInterval(this.timer);
      if (this.units) this.units.classList.add('drl-cd__units--hidden');
      if (this.expired) this.expired.classList.remove('drl-cd__expired--hidden');
      this.classList.add('is-expired');
      this.dispatchEvent(new CustomEvent('drl-countdown:expired', { bubbles: true }));
      return;
    }

    const days = Math.floor(left / 86400);
    left %= 86400;
    const hours = Math.floor(left / 3600);
    left %= 3600;
    const minutes = Math.floor(left / 60);
    const seconds = left % 60;

    this.daysUnit.forEach((el) => el.classList.toggle('is-zero', days === 0));
    this.set(this.out.days, String(days));
    this.set(this.out.hours, String(hours).padStart(2, '0'));
    this.set(this.out.minutes, String(minutes).padStart(2, '0'));
    this.set(this.out.seconds, String(seconds).padStart(2, '0'));
  }

  set(el, value) {
    if (!el || el.textContent === value) return;
    el.textContent = value;
    if (!this.softTick) return;
    // Restart the tick animation on the digit that changed, and only on that one.
    el.classList.remove('is-tick');
    void el.offsetWidth;
    el.classList.add('is-tick');
  }
}

if (!customElements.get('drl-countdown')) {
  customElements.define('drl-countdown', DrlCountdown);
}
